// Toolbar button to launch the Copilot chat panel.
// Gated by the `copilot_enabled` Flagsmith feature flag — renders nothing when off.
// Per plan Task 11.4 and spec § 12.5 / Cross-cutting § C.

import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react";
import {
  Sparkles,
  ListChecks,
  RefreshCw,
  Ellipsis,
  X,
  Sun,
  Moon,
  Minus,
  Maximize2,
  PanelRight,
  Eye,
  EyeOff,
} from "lucide-react";
import { CopilotController } from "../copilot/CopilotController";
import { ChatPanel } from "../copilot/chatPanel";
import {
  getSessionOptions,
  sessionStartErrorMessage,
  copilotSetupUrl,
  ACCESS_DENIED_MESSAGE,
} from "../copilot/apiClient";
import { registerController, unregisterController } from "../copilot/controllerRegistry";
import { getFlagValue } from "../feature_flags";
import { active_perm, active_vm_frame } from "../activeVM";
import { getPermissionDisplayName } from "../util/permissionName";

/** How long the transient failure toast stays up before clearing itself. */
const ERROR_TOAST_MS = 8000;
/** Longer for the setup toast: it carries a link the user has to reach for. */
const SETUP_TOAST_MS = 30000;

/**
 * @param {{ permissionId: number|string }} props
 *   permissionId - cloud_permission row id of the active connection.
 *   Kyoto resolves it to a vm_id when starting the session.
 */
export const CopilotButton = observer(({ permissionId }) => {
  const [flagEnabled, setFlagEnabled] = useState(false);
  const [controller, setController] = useState(null);
  const [open, setOpen] = useState(false);
  const [startError, setStartError] = useState(null);
  // Distinct from startError: Copilot was never configured for this account,
  // which is a setup task rather than a failure. Holds { managed } so the
  // message can either link an admin to the setup page or tell a managed
  // end-user to contact theirs. Kept separate because startError is a plain
  // string and this branch has to render a link.
  const [setupNeeded, setSetupNeeded] = useState(null);
  // Profile picker state — populated from GET /sessions/options on first open.
  // allowedSlots is the user's grant's allowed list (subset of fast|balanced|smart);
  // currentSlot is the slot of the live session.
  const [allowedSlots, setAllowedSlots] = useState([]);
  const [currentSlot, setCurrentSlot] = useState(null);
  // Non-null when the user clicked the Copilot button on a connection that
  // isn't where Copilot is currently pinned. The modal explains the choice
  // and routes to either "view it where it lives" or "stop it and start
  // fresh here". See specs/2026-05-15-copilot-connection-pinning-design.md
  // open question 3 (resolved 2026-05-15).
  const [repinChoice, setRepinChoice] = useState(null);

  // Check the feature flag once on mount (and whenever user context changes via OpenFeature).
  useEffect(() => {
    getFlagValue("copilot_enabled", false).then(setFlagEnabled);
  }, []);

  // Both messages render as a fixed-position toast over the VM, and neither
  // used to clear itself: the only reset was the next click on the Copilot
  // button, which re-fails and re-renders the same toast. Time them out.
  // The setup message gets much longer because it is actionable — pulling a
  // link out from under someone reaching for it is worse than a toast that
  // lingers. Both are also dismissable, below.
  // Declared before the early returns so the hook order stays stable.
  useEffect(() => {
    if (!startError) return undefined;
    const t = setTimeout(() => setStartError(null), ERROR_TOAST_MS);
    return () => clearTimeout(t);
  }, [startError]);

  useEffect(() => {
    if (!setupNeeded) return undefined;
    const t = setTimeout(() => setSetupNeeded(null), SETUP_TOAST_MS);
    return () => clearTimeout(t);
  }, [setupNeeded]);

  // When flag is off, render nothing — no HTTP calls, no UI.
  if (!flagEnabled) return null;

  const iframe = active_vm_frame.get();
  // No active VM session — don't show the button
  if (!iframe) return null;

  // Start (or restart under a different profile slot) the session. Creates a
  // fresh CopilotController each time, so conversation history clears on
  // profile switch — kyoto's CopilotSession is bound to one slot for its
  // lifetime, so reusing the controller would mix slots in one thread.
  // Where an admin configures Copilot, resolved per environment (prod/qa/devel)
  // rather than hardcoded: in the browser from denver's own host, under
  // Electron from the backend host baked into the build — the desktop renderer
  // runs on 127.0.0.1 and has nothing to derive from. Empty only when neither
  // is available (a dev server on localhost).
  const setupUrl = copilotSetupUrl();

  // Under Electron an in-page <a> would open inside the app shell; route it to
  // the system browser the same way the rest of the app does (see
  // openDesktopBrowser.js / auth.js).
  const openSetup = (e) => {
    if (window.isElectron && window.api?.open_link_external) {
      e.preventDefault();
      window.api.open_link_external(setupUrl);
    }
  };

  const startWithSlot = async (slot, slotsOverride) => {
    setStartError(null);
    setSetupNeeded(null);
    try {
      if (controller) {
        await controller.stop();
        unregisterController(controller.permissionId);
      }
      const ctrl = new CopilotController(iframe, permissionId);
      await ctrl.start(slot);
      // Sync slot state into the controller so the workflow surface can
      // pick a profile_slot when starting workflow sessions (B spec § 7.1a).
      ctrl.setDefaultSlots({
        defaultSlot: slot,
        allowedSlots: slotsOverride || allowedSlots,
      });
      ctrl.setSlot(slot);
      // Registration follows ctrl.start() so the sidebar's close-handler
      // only sees controllers with a live kyoto session_id. A failed start
      // throws before this line; the catch below clears the error state
      // and leaves the registry untouched.
      registerController(permissionId, ctrl);
      setController(ctrl);
      setCurrentSlot(slot);
      setOpen(true);
    } catch (e) {
      // profile_not_ready surfaces here rather than at /sessions/options: the
      // user has a grant, but the admin never finished configuring a profile.
      if (e?.setupRequired) setSetupNeeded({ managed: e.managed });
      else setStartError(sessionStartErrorMessage(e));
      console.error("[Copilot] session start failed:", e);
    }
  };

  const openCopilot = async () => {
    // Existing live controller? Two cases:
    //   1. User is already on the pinned connection — just show the panel.
    //   2. User is on a different connection — surface the repin chooser
    //      (the panel won't render anyway due to the auto-hide gate, so
    //      silently flipping `open` would be a dead-end click).
    if (controller && controller.sessionId) {
      const pinned = String(controller.permissionId);
      const current = String(permissionId);
      if (pinned === current) {
        setOpen(true);
        return;
      }
      setRepinChoice({ from: pinned, to: current });
      return;
    }
    setStartError(null);
    setSetupNeeded(null);
    try {
      const { defaultSlot, allowedSlots: allowed } = await getSessionOptions();
      if (!allowed.length || !defaultSlot) {
        setStartError("No Copilot profile available for your account.");
        return;
      }
      setAllowedSlots(allowed);
      await startWithSlot(defaultSlot, allowed);
    } catch (e) {
      // Kyoto flags the not-configured refusals with setup_required. Anything
      // else — network, 5xx, a real authorization failure — keeps #192's
      // messages, so a transient blip never sends someone to the setup page.
      if (e?.setupRequired) setSetupNeeded({ managed: e.managed });
      else setStartError(sessionStartErrorMessage(e));
      console.error("[Copilot] session start failed:", e);
    }
  };

  // "View where it's running" — switch the user to the connection Copilot is
  // pinned to. The panel's auto-hide gate then flips it back on automatically.
  const viewPinnedConnection = () => {
    if (!controller) return;
    active_perm.set(controller.permissionId);
    setRepinChoice(null);
    setOpen(true);
  };

  // "Stop and start here" — end the current session (which kyoto records as
  // `user_stopped`), then start a fresh one bound to the user's current
  // connection. Chat history is intentionally dropped — same UX as profile
  // switch and "New conversation".
  const repinHere = async () => {
    setRepinChoice(null);
    await startWithSlot(currentSlot);
  };

  const closeCopilot = () => setOpen(false);

  // "New conversation" — end the current kyoto session and bring up a fresh
  // CopilotController on the same profile slot. Conversation history is
  // intentionally dropped (kyoto's CopilotSession is single-thread; reusing
  // the controller after endSession would just produce a session-not-found).
  // The panel stays open so the user is dropped straight into the new
  // thread instead of having to reopen the panel.
  const newConversation = async () => {
    if (!controller) return;
    await startWithSlot(currentSlot);
  };

  return (
    <>
      <li>
        <button
          type="button"
          className="btn control-buttons"
          onClick={openCopilot}
          aria-label="Open AI Co-Pilot"
          title="AI Co-Pilot"
        >
          <span aria-hidden="true">✨</span> AI Co-Pilot
        </button>
      </li>

      {startError && (
        <div className="copilot-start-error">
          {startError}
          <button
            type="button"
            className="copilot-start-error__dismiss"
            onClick={() => setStartError(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {setupNeeded && (
        // Distinct testid: this branch and startError render identical copy in
        // the managed case, so tests need to tell which one produced it.
        <div className="copilot-start-error" data-testid="copilot-setup-needed">
          {setupNeeded.managed || !setupUrl ? (
            // A managed end-user cannot fix this: tokyo's dashboard middleware
            // bounces them off /copilot/, so linking there would dead-end.
            // Same copy covers a missing setupUrl — a dev server on localhost,
            // where there is no dashboard host to link to.
            ACCESS_DENIED_MESSAGE
          ) : (
            <>
              AI Co-Pilot isn’t set up yet.{" "}
              <a
                href={setupUrl}
                target="_blank"
                rel="noreferrer"
                onClick={openSetup}
              >
                Set it up
              </a>
            </>
          )}
          <button
            type="button"
            className="copilot-start-error__dismiss"
            onClick={() => setSetupNeeded(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Auto-hide when the user is viewing a different connection than the
        * one Copilot is pinned to. The controller (and its kyoto session) stay
        * alive — the panel just reappears automatically when the user returns
        * to the pinned connection. See
        * specs/2026-05-15-copilot-connection-pinning-design.md § 7. */}
      {open && controller
        && String(active_perm.get()) === String(controller.permissionId)
        && (
        <CopilotPanel
          controller={controller}
          pinnedConnectionName={getPermissionDisplayName(controller.permissionId)}
          onClose={closeCopilot}
          onNewConversation={newConversation}
          allowedSlots={allowedSlots}
          currentSlot={currentSlot}
          onSlotChange={startWithSlot}
        />
      )}

      {repinChoice && createPortal((
        <div
          className="copilot-modal-backdrop copilot-modal-backdrop--fullscreen"
          onClick={() => setRepinChoice(null)}
        >
          <div
            role="alertdialog"
            aria-labelledby="copilot-repin-title"
            aria-describedby="copilot-repin-body"
            className="copilot-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="copilot-repin-title" className="copilot-modal-title">
              Copilot is on another connection
            </h3>
            <p id="copilot-repin-body" className="copilot-modal-body">
              Copilot is currently running on{' '}
              <strong>{getPermissionDisplayName(repinChoice.from)}</strong>.
              You can switch back to that connection to keep the conversation,
              or stop it and start a new one on{' '}
              <strong>{getPermissionDisplayName(repinChoice.to)}</strong> —
              the current chat history will be cleared.
            </p>
            <div className="copilot-modal-buttons">
              <button
                type="button"
                className="copilot-modal-btn copilot-modal-btn--ghost"
                onClick={() => setRepinChoice(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="copilot-modal-btn copilot-modal-btn--ghost"
                onClick={viewPinnedConnection}
                autoFocus
              >
                View where it's running
              </button>
              <button
                type="button"
                className="copilot-modal-btn copilot-modal-btn--primary"
                onClick={repinHere}
              >
                Stop &amp; start here
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </>
  );
});

/**
 * Floating panel wrapper around ChatPanel with close / stop controls.
 *
 * Layout model:
 *   - Floating mode (default): CSS anchors the panel by `right` + `top`.
 *     Header drag adds an inline transform offset. Width is user-controlled
 *     via left/right edge resize handles; height stays CSS-driven so it
 *     adapts to the viewport.
 *   - Docked mode: panel snaps to the right edge full-height. Drag and the
 *     transform offset are suspended; only width is resizable (and only from
 *     the left edge, since the right edge sits on the screen border).
 *
 * `userWidth` is the single source of truth for panel width and survives
 * dock toggles in both directions — resizing while docked carries over to
 * floating mode, and vice versa.
 *
 * Pointer capture on the header keeps the drag alive when the cursor crosses
 * the VM iframe (which would otherwise steal events).
 */
const PANEL_DEFAULT_W = 520;
const PANEL_MIN_W = 320;
const PANEL_DEFAULT_H = 720;
const PANEL_MIN_H = 320;

const DOCK_STORAGE_KEY = "copilot:docked";
function readDockPref() {
  try { return localStorage.getItem(DOCK_STORAGE_KEY) === "1"; }
  catch (_) { return false; }
}
function writeDockPref(value) {
  try { localStorage.setItem(DOCK_STORAGE_KEY, value ? "1" : "0"); }
  catch (_) { /* non-fatal */ }
}

// Theme: "dark" (default) | "light". Persisted per-browser. First visit with
// no stored choice follows the OS prefers-color-scheme. Applied as the
// data-copilot-theme attribute on <html> — only --cop-* tokens key off it, so
// the rest of the Webconnect/Tokyo app is unaffected.
const THEME_STORAGE_KEY = "copilot:theme";
function readThemePref() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch (_) { /* fall through to system preference */ }
  try {
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) {
      return "light";
    }
  } catch (_) { /* default below */ }
  return "dark";
}
function applyTheme(theme) {
  try {
    const el = document.documentElement;
    if (theme === "light") el.setAttribute("data-copilot-theme", "light");
    else el.removeAttribute("data-copilot-theme");
  } catch (_) { /* SSR / no document — non-fatal */ }
}
function writeThemePref(theme) {
  try { localStorage.setItem(THEME_STORAGE_KEY, theme); }
  catch (_) { /* non-fatal */ }
}

export const CopilotPanel = observer(({ controller, pinnedConnectionName, onClose, onNewConversation, allowedSlots, currentSlot, onSlotChange }) => {
  const [pos, setPos] = useState({ dx: 0, dy: 0 });
  const [userWidth, setUserWidth] = useState(PANEL_DEFAULT_W);
  const [userHeight, setUserHeight] = useState(PANEL_DEFAULT_H);
  const [docked, setDocked] = useState(readDockPref);
  const [minimized, setMinimized] = useState(false);
  const [theme, setTheme] = useState(readThemePref);
  // Overflow menu visibility — the single-row header now stashes
  // theme/dock/minimize/compact toggles behind a "⋯" button so the
  // primary chrome stays minimal. Click-outside dismisses.
  //
  // The menu is portaled to <body> (see createPortal usage below) and
  // positioned via getBoundingClientRect of the trigger, because the
  // panel has `overflow: hidden` + a `transform` in floating mode —
  // either would clip an in-place absolute/fixed dropdown. Portal +
  // viewport coords sidesteps both. menuRef tags the portaled node so
  // click-outside knows menu clicks aren't "outside".
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);  // { top, right } | null
  const overflowRef = useRef(null);
  const menuRef = useRef(null);
  // Pending slot during a switch confirmation. Non-null while the modal is
  // up. The controlled <select> stays bound to currentSlot, so cancelling
  // (setting pendingSlot back to null) re-renders the picker back to its
  // pre-attempt value without a manual revert.
  const [pendingSlot, setPendingSlot] = useState(null);
  /* Measured at runtime from the actual .the-topbar element so the docked
   * panel sits flush against the toolbar regardless of its current rendered
   * height. Falls back to 80px (matches the floating-mode offset) if the
   * topbar isn't found, which keeps the panel safely below standard chrome. */
  const [topbarBottom, setTopbarBottom] = useState(80);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);

  useLayoutEffect(() => {
    const measure = () => {
      const bar = document.querySelector(".the-topbar");
      if (!bar) return;
      setTopbarBottom(Math.max(0, Math.round(bar.getBoundingClientRect().bottom)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Header drag — suspended while docked. Interactive elements inside the
  // header (.copilot-panel-controls buttons, the .copilot-panel-slot
  // <select>, and the top-row close button) must NOT call preventDefault
  // here, otherwise their native click / dropdown-open behavior is
  // swallowed by the drag start.
  //
  // The overflow menu is portaled to <body>, so its items live OUTSIDE the
  // header's DOM subtree — but React still bubbles their synthetic events
  // up the React tree to this handler. Without the menu selector below,
  // pointerdown on a menu item falls through every other guard, the header
  // captures the pointer, and pointerup/click get re-targeted to the
  // header instead of the menu item — so no menu button ever fires.
  const onPointerDown = (e) => {
    if (docked) return;
    if (e.target.closest(".copilot-panel-controls, .copilot-panel-slot, .copilot-panel-btn--close, .copilot-panel-overflow-menu")) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { mx: e.clientX, my: e.clientY, dx: pos.dx, dy: pos.dy };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setPos({
      dx: dragRef.current.dx + (e.clientX - dragRef.current.mx),
      dy: dragRef.current.dy + (e.clientY - dragRef.current.my),
    });
  };
  const onPointerUp = (e) => {
    if (!dragRef.current) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
  };

  // Resize handles. `edge` is "w" (left), "e" (right), or "s" (bottom).
  //   west: width grows as cursor moves leftward (right edge stays put).
  //   east: width grows as cursor moves rightward; we shift the drag offset
  //         so the LEFT edge stays put, which is what feels natural for a
  //         right-edge handle on a right-anchored panel.
  //   south: height grows as cursor moves downward (top edge stays put).
  //          Only meaningful in floating mode — docked mode height is
  //          viewport-driven so the south handle isn't rendered.
  const onResizeDown = (edge) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    resizeRef.current = {
      edge,
      mx: e.clientX,
      my: e.clientY,
      w0: userWidth,
      h0: userHeight,
      dx0: pos.dx,
    };
    // While dragging, soak pointer events on the iframe area. In docked
    // side-by-side mode the iframe sits flush with the west handle — the
    // first pointermove crosses into the iframe and (despite
    // setPointerCapture) the Guacamole canvas inside reliably preempts
    // ours, so the drag locks up after ~1px. Setting this attr drives a
    // `body[data-copilot-resizing="1"] .webconnect-frame { pointer-events:
    // none }` rule in copilot.css that lasts only for the drag.
    document.body.dataset.copilotResizing = "1";
  };
  const onResizeMove = (e) => {
    if (!resizeRef.current) return;
    const { edge, mx, my, w0, h0, dx0 } = resizeRef.current;
    if (edge === "s") {
      const dy = e.clientY - my;
      const maxH = Math.max(PANEL_MIN_H, window.innerHeight - 40);
      const h = Math.max(PANEL_MIN_H, Math.min(maxH, h0 + dy));
      setUserHeight(h);
      return;
    }
    const delta = e.clientX - mx;
    const maxW = Math.max(PANEL_MIN_W, window.innerWidth - 40);
    if (edge === "w") {
      const w = Math.max(PANEL_MIN_W, Math.min(maxW, w0 - delta));
      setUserWidth(w);
      // Right edge stays put (panel is right-anchored); no pos change.
    } else {
      const w = Math.max(PANEL_MIN_W, Math.min(maxW, w0 + delta));
      setUserWidth(w);
      if (!docked) {
        // Floating mode: panel is right-anchored, so growing rightward would
        // visually push the left edge leftward (because right stays fixed).
        // Compensate via dx so the LEFT edge stays under the cursor anchor.
        setPos({ dx: dx0 + (w - w0), dy: pos.dy });
      }
    }
  };
  const onResizeUp = (e) => {
    if (!resizeRef.current) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    resizeRef.current = null;
    delete document.body.dataset.copilotResizing;
  };

  const toggleDock = () => setDocked((d) => { const next = !d; writeDockPref(next); return next; });
  // Minimizing a docked panel is incoherent — the iframe is sized for a
  // tall side-by-side panel, then suddenly the panel collapses to ~80 px
  // and the dock margin still reserves the full width. Cleanest UX:
  // entering mini mode releases the dock first. Exiting mini doesn't
  // re-dock (user opted into mini, give them the floating panel back).
  const toggleMinimize = () => setMinimized((m) => {
    const next = !m;
    if (next && docked) {
      setDocked(false);
      writeDockPref(false);
    }
    return next;
  });
  const toggleTheme = () => setTheme((t) => {
    const next = t === "light" ? "dark" : "light";
    writeThemePref(next);
    return next;
  });

  // Apply the theme to <html> whenever it changes, and clean up on unmount so
  // a closed panel doesn't leave the host app in light mode.
  useEffect(() => {
    applyTheme(theme);
    return () => applyTheme("dark");
  }, [theme]);

  // Side-by-side dock: when docked, publish the panel width to <body> so
  // the global rule `body[data-copilot-docked="1"] .the-right-section
  // { margin-right: var(--copilot-dock-w) }` (in copilot.css) pulls the
  // VM iframe out from under the panel. Guacamole's canvas needs an
  // explicit resync after the layout shift — dispatching a window resize
  // event triggers webconnect/resize.js#inject_resize through its 400ms
  // debounce (so drag bursts collapse to a single VM resize).
  //
  // Cleanup is split into a mount-only effect (below) so the per-update
  // path doesn't tear down + re-apply the attribute on every userWidth
  // tick during a drag, which would flicker the iframe.
  useEffect(() => {
    const body = document.body;
    if (docked) {
      body.dataset.copilotDocked = "1";
      body.style.setProperty("--copilot-dock-w", `${userWidth}px`);
    } else {
      delete body.dataset.copilotDocked;
      body.style.removeProperty("--copilot-dock-w");
    }
    window.dispatchEvent(new Event("resize"));
  }, [docked, userWidth]);

  // Unmount-only cleanup: panel closed or unmounted while docked must
  // not leave the iframe shifted left or stuck with disabled pointer
  // events (the resizing attr would persist if the panel unmounts
  // between pointerdown and pointerup).
  useEffect(() => () => {
    delete document.body.dataset.copilotDocked;
    delete document.body.dataset.copilotResizing;
    document.body.style.removeProperty("--copilot-dock-w");
    window.dispatchEvent(new Event("resize"));
  }, []);

  // Close the overflow menu on outside-click. Because the menu is portaled
  // outside the trigger's subtree, we identify "inside" via class selectors
  // rather than ref.contains() — ref-based checks have a tiny window during
  // open where menuRef hasn't been populated yet, and a missed inside-check
  // would close the menu before its own click handler runs. selectors via
  // .closest() are robust to that timing.
  //
  // We listen on mousedown (capture phase) so we beat the menu item's own
  // synthetic click, but only CLOSE on outside — inside clicks still fire
  // the menu items' onClick normally on the subsequent click event.
  useEffect(() => {
    if (!overflowOpen) return undefined;
    const onDown = (e) => {
      const inside = e.target.closest?.(
        ".copilot-panel-overflow-menu, .copilot-panel-overflow",
      );
      if (!inside) setOverflowOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [overflowOpen]);

  // While a recording is in progress, a *floating* panel gets out of the way
  // so the user can work on the VM with only the pill visible. A docked panel
  // sits alongside the screen without obstructing it, so we leave it up
  // (hiding it would just leave its reserved space empty). ChatPanel stays
  // mounted while hidden — it portals the pill to <body> — so the panel
  // reappears the moment recording stops and the review wizard opens.
  const isRecording =
    controller.recordingController &&
    controller.recordingController.stage === "recording";

  const className = [
    "copilot-panel",
    docked && "copilot-panel--docked",
    minimized && "copilot-panel--mini",
    controller.busy && "copilot-panel--thinking",
    controller.compact && "copilot-panel--compact",
    isRecording && !docked && "copilot-panel--recording",
  ]
    .filter(Boolean)
    .join(" ");

  const style = docked
    ? {
        width: `${userWidth}px`,
        top: `${topbarBottom}px`,
        maxHeight: `calc(100vh - ${topbarBottom}px)`,
      }
    : {
        width: `${userWidth}px`,
        height: `${userHeight}px`,
        transform: `translate(${pos.dx}px, ${pos.dy}px)`,
      };

  return (
    <div className={className} style={style}>
      <div
        className="copilot-panel-header"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {/* One-row header. Identity on the left, slot picker in the middle,
            primary icon actions on the right. Theme / dock / minimize /
            compact live under the "⋯" overflow so the chrome stays minimal
            but the controls are still reachable. */}
        <span
          className="copilot-panel-title"
          title={pinnedConnectionName ? `Pinned to ${pinnedConnectionName}` : undefined}
        >
          <span className="copilot-panel-title-glyph" aria-hidden="true">
            <Sparkles className="copilot-panel-title-glyph-icon" />
          </span>
          <span className="copilot-panel-title-text">AI Co-Pilot</span>
          <span
            className="copilot-panel-pulse-dot"
            aria-hidden="true"
            title="Connected"
          />
        </span>

        {allowedSlots && allowedSlots.length > 1 && currentSlot && (
          // Profile picker. Disabled while busy — switching slot starts a new
          // kyoto session (sessions are bound to one slot for their lifetime),
          // which clears conversation history, so we don't let the user
          // trigger that mid-turn. The confirm modal (rendered below) guards
          // the destructive history-clearing action.
          <select
            className="copilot-panel-slot"
            value={currentSlot}
            disabled={controller.busy}
            onChange={(e) => {
              const next = e.target.value;
              if (next === currentSlot) return;
              setPendingSlot(next);
            }}
            title="Copilot profile — switching starts a new session"
            aria-label="Copilot profile"
          >
            {allowedSlots.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}

        <div className="copilot-panel-controls">
          <button
            className="copilot-panel-btn copilot-panel-btn--icon"
            onClick={() => controller.openWorkflowsLibrary()}
            title="Browse and run saved workflows"
            aria-label="Workflows"
            disabled={controller.busy}
          >
            <ListChecks className="copilot-panel-icon" />
          </button>
          <button
            className="copilot-panel-btn copilot-panel-btn--icon"
            onClick={onNewConversation}
            title="End this conversation and start a new one"
            aria-label="New conversation"
            disabled={controller.busy}
          >
            <RefreshCw className="copilot-panel-icon" />
          </button>
          <div className="copilot-panel-overflow" ref={overflowRef}>
            <button
              className="copilot-panel-btn copilot-panel-btn--icon"
              onClick={(e) => {
                if (overflowOpen) { setOverflowOpen(false); return; }
                // Anchor the portaled menu below the trigger's bottom-right.
                const r = e.currentTarget.getBoundingClientRect();
                setMenuPos({
                  top: Math.round(r.bottom + 6),
                  right: Math.round(window.innerWidth - r.right),
                });
                setOverflowOpen(true);
              }}
              title="More options"
              aria-label="More options"
              aria-haspopup="menu"
              aria-expanded={overflowOpen}
            >
              <Ellipsis className="copilot-panel-icon" />
            </button>
            {overflowOpen && menuPos && createPortal(
              <div
                ref={menuRef}
                className="copilot-panel-overflow-menu"
                role="menu"
                style={{ top: menuPos.top, right: menuPos.right }}
              >
                <button
                  type="button"
                  role="menuitem"
                  className="copilot-panel-overflow-item"
                  onClick={() => { toggleTheme(); setOverflowOpen(false); }}
                >
                  {theme === "light"
                    ? <Moon className="copilot-panel-icon" />
                    : <Sun className="copilot-panel-icon" />}
                  <span>{theme === "light" ? "Dark theme" : "Light theme"}</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="copilot-panel-overflow-item"
                  onClick={() => { toggleDock(); setOverflowOpen(false); }}
                  aria-pressed={docked}
                >
                  <PanelRight className="copilot-panel-icon" />
                  <span>{docked ? "Undock panel" : "Dock to right edge"}</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="copilot-panel-overflow-item"
                  onClick={() => { toggleMinimize(); setOverflowOpen(false); }}
                  aria-pressed={minimized}
                >
                  {minimized
                    ? <Maximize2 className="copilot-panel-icon" />
                    : <Minus className="copilot-panel-icon" />}
                  <span>{minimized ? "Expand panel" : "Minimize panel"}</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="copilot-panel-overflow-item"
                  onClick={() => { controller.toggleCompact(); setOverflowOpen(false); }}
                  aria-pressed={controller.compact}
                >
                  {controller.compact
                    ? <Eye className="copilot-panel-icon" />
                    : <EyeOff className="copilot-panel-icon" />}
                  <span>
                    {controller.compact ? "Show agent details" : "Hide agent details"}
                  </span>
                </button>
              </div>,
              document.body,
            )}
          </div>
          <button
            className="copilot-panel-btn copilot-panel-btn--close"
            onClick={onClose}
            title="Hide panel"
            aria-label="Close Copilot panel"
          >
            <X className="copilot-panel-icon" />
          </button>
        </div>
      </div>

      <ChatPanel controller={controller} />

      {pendingSlot && (
        <div
          className="copilot-modal-backdrop"
          onClick={() => setPendingSlot(null)}
        >
          <div
            role="alertdialog"
            aria-labelledby="copilot-switch-title"
            aria-describedby="copilot-switch-body"
            className="copilot-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="copilot-switch-title" className="copilot-modal-title">
              Switch profile?
            </h3>
            <p id="copilot-switch-body" className="copilot-modal-body">
              Switching to <strong>{pendingSlot}</strong> will start a new
              conversation. The current chat history will be cleared.
            </p>
            <div className="copilot-modal-buttons">
              <button
                type="button"
                className="copilot-modal-btn copilot-modal-btn--ghost"
                onClick={() => setPendingSlot(null)}
                autoFocus
              >
                Cancel
              </button>
              <button
                type="button"
                className="copilot-modal-btn copilot-modal-btn--primary"
                onClick={() => {
                  const slot = pendingSlot;
                  setPendingSlot(null);
                  onSlotChange(slot);
                }}
              >
                Switch &amp; clear chat
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="copilot-resize copilot-resize--w"
        title="Resize width"
        aria-label="Resize panel width (left edge)"
        onPointerDown={onResizeDown("w")}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeUp}
      />
      {!docked && (
        <div
          className="copilot-resize copilot-resize--e"
          title="Resize width"
          aria-label="Resize panel width (right edge)"
          onPointerDown={onResizeDown("e")}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
        />
      )}
      {!docked && (
        <div
          className="copilot-resize copilot-resize--s"
          title="Resize height"
          aria-label="Resize panel height (bottom edge)"
          onPointerDown={onResizeDown("s")}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
        />
      )}
    </div>
  );
});
